(function (root, factory) {
  'use strict';
  const rules = factory();
  if (typeof module === 'object' && module.exports) module.exports = rules;
  else root.SissiluxWarrantyRules = rules;
})(typeof globalThis === 'object' ? globalThis : this, function () {
  'use strict';

  function id(value) {
    if (typeof value === 'number' && !Number.isSafeInteger(value)) {
      throw new TypeError('Shopify IDs must be safe integers or strings.');
    }
    const result = String(value == null ? '' : value).replace(/^gid:\/\/shopify\/(?:Product|ProductVariant)\//, '');
    if (!/^[1-9]\d*$/.test(result)) throw new TypeError('A numeric Shopify ID is required.');
    return result;
  }

  function cents(value) {
    const text = String(value);
    if (!/^\d+(?:\.\d{1,2})?$/.test(text)) throw new TypeError('USD prices must have at most two decimal places.');
    const parts = text.split('.');
    const result = Number(parts[0]) * 100 + Number((parts[1] || '').padEnd(2, '0'));
    if (!Number.isSafeInteger(result)) throw new RangeError('USD price is outside the supported range.');
    return result;
  }

  function integer(value, name, minimum) {
    if (!Number.isSafeInteger(value) || value < minimum) {
      throw new TypeError(name + ' must be a safe integer of at least ' + minimum + '.');
    }
    return value;
  }

  function createConfiguration(input) {
    if (!Array.isArray(input.warrantyVariants) || !input.warrantyVariants.length) throw new TypeError('At least one warranty variant is required.');
    const variants = input.warrantyVariants.map(variant => ({variantId:id(variant.id), sku:String(variant.sku || '').trim(), priceCents:cents(variant.price)}));
    const lines = String(input.tierTable || '').split(/\r?\n/).map(line => line.trim()).filter(Boolean);
    if (!lines.length) throw new TypeError('A USD warranty tier table is required.');
    const ceilings = new Set();
    const chosen = new Set();
    const tiers = lines.map(line => {
      const parts = line.split('|').map(part => part.trim());
      if (parts.length !== 2 || !parts[1]) throw new TypeError('Each tier must be maximum USD amount | variant SKU.');
      const maximumUsdCents = cents(parts[0]);
      const matches = variants.filter(variant => variant.sku === parts[1]);
      if (matches.length !== 1) throw new TypeError('Tier SKU must match exactly one warranty variant: ' + parts[1]);
      const variant = matches[0];
      if (ceilings.has(maximumUsdCents) || chosen.has(variant.variantId)) throw new TypeError('Tier amounts and variant SKUs must be unique.');
      ceilings.add(maximumUsdCents); chosen.add(variant.variantId);
      return Object.freeze({...variant, maximumUsdCents});
    }).sort((a,b) => a.maximumUsdCents - b.maximumUsdCents);
    return Object.freeze({
      warrantyProductId:id(input.warrantyProductId),
      legacyWarrantyProductIds:Object.freeze((input.legacyWarrantyProductIds || []).map(id)),
      minimumUsdSubtotalCents:integer(input.minimumUsdSubtotalCents || 0, "Minimum USD subtotal", 0),
      maximumUsdSubtotalCents:integer(input.maximumUsdSubtotalCents || Number.MAX_SAFE_INTEGER, "Maximum USD subtotal", 0),
      warrantyVariantIds:Object.freeze(variants.map(variant => variant.variantId)),
      eligibleProductIds:Object.freeze(Array.from(new Set(input.eligibleProductIds.map(id)))),
      tiers:Object.freeze(tiers)
    });
  }

  // The caller supplies authoritative USD cents for this exact cart snapshot.
  // Localized cart totals and exchange-rate estimation do not belong in these rules.
  function compute(cart, configuration, optedOut, amount) {
    if (!cart || !Array.isArray(cart.items)) throw new TypeError('A complete cart items array is required.');
    if (typeof optedOut !== 'boolean') throw new TypeError('Opt-out state must be explicit.');
    const eligibleIds = new Set(configuration.eligibleProductIds);
    const warrantyIds = new Set(configuration.warrantyVariantIds);
    const warrantyLines = [];
    const seenKeys = new Set();
    let eligibleQuantity = 0;

    cart.items.forEach(function (line) {
      const quantity = integer(line.quantity, 'Cart quantity', 1);
      const productId = id(line.product_id);
      const variantId = id(line.variant_id == null ? line.id : line.variant_id);
      const isWarranty = productId === configuration.warrantyProductId || configuration.legacyWarrantyProductIds.includes(productId) || warrantyIds.has(variantId);
      if (isWarranty) {
        if (typeof line.key !== 'string' || !line.key || seenKeys.has(line.key)) {
          throw new TypeError('Warranty lines require unique current line keys.');
        }
        seenKeys.add(line.key);
        warrantyLines.push({ key: line.key, variantId: variantId, quantity: quantity });
      } else if (eligibleIds.has(productId)) {
        eligibleQuantity += quantity;
        integer(eligibleQuantity, 'Eligible quantity', 1);
      }
    });

    const shouldCover = eligibleQuantity > 0 && !optedOut;
    const suppliedSubtotal = amount && amount.eligibleUsdSubtotalCents;
    const subtotal = suppliedSubtotal == null && !shouldCover ? 0 : integer(suppliedSubtotal, 'Eligible USD subtotal cents', 0);
    let tierIndex = null;
    const coverageEligible = eligibleQuantity > 0 && subtotal >= configuration.minimumUsdSubtotalCents && subtotal <= configuration.maximumUsdSubtotalCents;
    if (shouldCover && coverageEligible) {
      const index = configuration.tiers.findIndex(tier => subtotal <= tier.maximumUsdCents);
      tierIndex = index < 0 ? configuration.tiers.length - 1 : index;
    }
    const targetVariantId = tierIndex == null ? null : configuration.tiers[tierIndex].variantId;
    const candidates = warrantyLines.filter(function (line) { return line.variantId === targetVariantId; });
    const keep = candidates.find(function (line) { return line.quantity === 1; }) || candidates[0];
    const updates = {};

    warrantyLines.forEach(function (line) {
      if (line !== keep) updates[line.key] = 0;
      else if (line.quantity !== 1) updates[line.key] = 1;
    });
    if (targetVariantId && !keep) updates[targetVariantId] = 1;

    return {
      targetVariantId: targetVariantId,
      coverageEligible: coverageEligible,
      tierIndex: tierIndex,
      updates: updates,
      changed: Object.keys(updates).length > 0,
      eligibleQuantity: eligibleQuantity,
      eligibleUsdSubtotalCents: subtotal
    };
  }

  return Object.freeze({ createConfiguration: createConfiguration, compute: compute });
});

